/**
 * @file Defines the wrapping layout for all other pages.
 *
 * We can add shared stylesheets and set the charset here.
 */

import "../styles/base.scss";
import "../styles/themes.scss";
import "../styles/typography.scss";
import "../styles/layout.scss";
import "../styles/logo.scss";
import "../styles/embla-carousel.scss";
import "../styles/squiggly.scss";
import "react-toastify/dist/ReactToastify.css";

import { Slide, ToastContainer } from "react-toastify";

import { AnimatePresence } from "framer-motion";
import BannerView from "../components/BannerView";
import CartContextProvider from "../contexts/CartContext";
import Content from "../content";
import Footer from "../components/Footer";
import Head from "next/head";
import Navigation from "../components/Navigation";
import NavigationContextProvider from "../hooks/contexts/NavigationContext";
import PhysicalProductsProvider from "../contexts/PhysicalProductsContext";
import { SessionProvider } from "next-auth/react";
import Settings from "../settings";
import ShareCartContextProvider from "../contexts/ShareCartContext";
import ShareCartModal from "../components/cart/ShareCartModal";
import { getActiveBanners } from "../util/banners";
import { push } from "../util/routing";
import { useEffect } from "react";

function App({ Component, pageProps: { session, ...pageProps }, router }) {
  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_ENABLE_FOUNDRY !== "true" &&
      Component.displayName?.includes("Fonts")
    ) {
      push(router, "/");
    }
  }, []);

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <title>{Content.siteName}</title>
        <meta name="description" content={Content.siteDescription} />
        <meta property="og:title" content={Content.siteName} />
        <meta property="og:description" content={Content.siteDescription} />
        <meta property="og:url" content={Content.siteUrl} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://shop.spacetypeco.com/img/social_image.png"
        />
        <meta property="twitter:title" content={Content.siteName} />
        <meta
          property="twitter:description"
          content={Content.siteDescription}
        />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:creator" content="@spacetypeco" />
        <meta
          property="twitter:image"
          content="https://shop.spacetypeco.com/img/social_image.png"
        />
        <meta property="twitter:image:alt" content="Space Type logo" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Generate favicons from https://realfavicongenerator.net/ */}
        <meta charSet="utf-8" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicons/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicons/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicons/favicon-16x16.png"
        />
        {/* <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5BBAD5" />
        <meta name="msapplication-TileColor" content="#DA532C" />
        <meta name="theme-color" content="#FFFFFF" /> */}
      </Head>
      <SessionProvider session={session}>
        <NavigationContextProvider>
          <PhysicalProductsProvider>
            <CartContextProvider>
              <ShareCartContextProvider>
                <ShareCartModal />
                {getActiveBanners().map((banner) => {
                  return <BannerView banner={banner} />;
                })}
                <div
                  className="relative"
                  style={{
                    backgroundColor: Settings.isPaperBg(Component.displayName)
                      ? Settings.paperBgColor(Component.displayName)
                      : "var(--color-secondary)",
                    isolation: "isolate",
                    transition: "background-color 0.25s",
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        'url("/img/speckletone-truewhite.webp")',
                      backgroundBlendMode: Settings.paperBgBlendMode(
                        Component.displayName
                      ),
                      opacity: Settings.usePaperImage(Component.displayName)
                        ? 1
                        : 0,
                      // transition: "opacity 0.25s",
                      pointerEvents: "none",
                      zIndex: -1,
                    }}
                  />
                  <div
                    aria-hidden
                    className={`app-riso-overlay${
                      Settings.useRiso(Component.displayName)
                        ? " app-riso-overlay--active"
                        : ""
                    }`}
                  />
                  <Navigation activeComponentName={Component.displayName} />
                  <main
                    className="flex flex-col justify-start min-h-screen"
                    style={{
                      // Hack to force sticky navs to work
                      display:
                        Component.displayName == "FontsFamily" ? "initial" : "",
                    }}
                  >
                    <ToastContainer
                      position="top-right"
                      autoClose={4000}
                      hideProgressBar
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss={false}
                      draggable={false}
                      pauseOnHover={true}
                      theme="light"
                      transition={Slide}
                    />
                    <AnimatePresence
                      mode="wait"
                      initial={false}
                      onExitComplete={() => {
                        window.scrollTo(0, 0);
                      }}
                    >
                      <Component {...pageProps} key={router.asPath} />
                    </AnimatePresence>
                  </main>
                  <Footer />
                </div>
              </ShareCartContextProvider>
            </CartContextProvider>
          </PhysicalProductsProvider>
        </NavigationContextProvider>
      </SessionProvider>
    </>
  );
}

export default App;
